import {
  useCurrentAccount,
  useFormatBreederSuggestions,
  useGetBuyerApplications,
  useGetSuggestedBreeders,
} from '@application/api';
import {
  MtApplicationsBuyerPayModalController,
  MtApplicationsOwnModalController,
  MtApplicationsSuggestionModalController,
  useApplicationMessageModal,
} from '@application/components';
import { useConfirmModal, useModal, useRouter } from '@application/utils';
import { useApplicationsShared } from '@application/utils/hooks/useApplicationsShared';
import { DogBreederApplicationStatus } from '@gql/graphql';
import type { ActionsPopperData, ActionsPopperItem } from '@presentation/components';
import { ActionsPopper, ConfirmationModal } from '@presentation/components';
import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { MtApplicationsPage } from '@presentation/pages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCheckoutModal } from '../../MtApplicationsBuyerPayModalController/useCheckoutModal';
import { useFormatBreederCard } from '../BreederListPageController/api/useFormatBreederCard';
import { useCancelApplication } from '../MtApplicationBuyerPageController/api/useCancelApplication';
import { useGetConversations } from '../MtMessagesPageController/api/useGetConversations';
import { useFormatApplications } from './useFormatApplications';

export const MtApplicationsBuyerPageController = () => {
  const { translate, navigateToRoute } = useRouter();
  const { setAccountId, show } = useApplicationMessageModal();
  const [showArchived, setShowArchived] = useState(false);
  const { currentAccount } = useCurrentAccount();

  const { applications, applicationsLoading } = useGetBuyerApplications(showArchived);
  const { conversations } = useGetConversations();
  const { cancelApplication, cancelApplicationIsLoading } = useCancelApplication();
  const {
    popperItems: sharedPopperItems,
    selectedApplication,
    setSelectedApplicationId,
    popper,
  } = useApplicationsShared({
    applications,
  });

  const hasRemainingDaysForFreeApplication = useMemo(() => {
    if (currentAccount?.accountRemainingDaysForFreeApplications) {
      return currentAccount?.accountRemainingDaysForFreeApplications > 0;
    }
    return false;
  }, [currentAccount?.accountRemainingDaysForFreeApplications]);

  const onActionsClick = (application: any) => (event: any) => {
    popper.setTriggerElement(event.target as any);
    setSelectedApplicationId(application.id);
    popper.toggle();
  };

  const { formattedApplicationsCards } = useFormatApplications({
    applications: applications.filter((application) => application.seenByBuyer),
    onActionsClick,
    hasRemainingDaysForFreeApplication,
  });

  const { formattedApplicationsCards: formattedNewApplicationsCards } = useFormatApplications({
    applications: applications.filter((application) => !application.seenByBuyer),
    onActionsClick,
    hasRemainingDaysForFreeApplication,
  });

  const { checkoutApplicationClickHandler, checkoutApplicationModalProps } = useCheckoutModal(
    selectedApplication?.id,
    {
      buyerImage: currentAccount?.imgs?.[0]?.small || PLACEHOLDER_IMAGE_USER,
      breederImage: selectedApplication?.dogBreeder.profileImg || PLACEHOLDER_IMAGE_USER,
    },
  );

  const { modal: confirmCancelApplicationModal, confirmedShow, confirmHandler } = useConfirmModal();
  const { show: showOwnModal, modal: ownModal } = useModal();
  const { show: showSuggestionsModal, modal: suggestionsModal } = useModal();

  const { breeders } = useGetSuggestedBreeders(
    [...new Set(applications.map((a) => a.dogBreeder.id))],
    applications.map((a) => a.breedPreference.id),
  );
  const { formatBreederSuggestions } = useFormatBreederSuggestions(
    applications.map((a) => a.breedPreference.id),
  );
  const { formatBreeder } = useFormatBreederCard();

  const otherAccountId: number = useMemo(() => {
    return selectedApplication?.account?.id ?? selectedApplication?.dogBreeder?.id;
  }, [selectedApplication?.account?.id, selectedApplication?.dogBreeder?.id]);

  const existingConversationId: number | undefined = useMemo(() => {
    return conversations.find((x) => x.otherAccountId === otherAccountId)?.id;
  }, [conversations, otherAccountId]);

  useEffect(() => {
    setAccountId(otherAccountId);
  }, [otherAccountId, setAccountId]);

  const cancelClickHandler = useCallback(async () => {
    if (selectedApplication) {
      const confirmed = await confirmedShow();
      if (!confirmed) return;
      await cancelApplication(selectedApplication.id);
    }
  }, [selectedApplication, confirmedShow, cancelApplication]);

  const isLoading = useMemo(() => {
    return applicationsLoading || cancelApplicationIsLoading;
  }, [applicationsLoading, cancelApplicationIsLoading]);

  const popperItems = useMemo((): ActionsPopperData['items'] => {
    return [
      ...(selectedApplication?.status === DogBreederApplicationStatus.CONTACT_AVAILABLE
        ? [
            {
              label: translate('common.buttons.own'),
              iconName: 'dog',
              onClick: () => {
                popper.close();
                showOwnModal();
              },
            } as ActionsPopperItem,
          ]
        : []),
      ...(selectedApplication?.status === DogBreederApplicationStatus.AWAITING_PAYMENT
        ? [
            {
              label: translate('common.buttons.pay'),
              iconName: 'wallet',
              onClick: () => {
                popper.close();
                checkoutApplicationClickHandler();
              },
            } as ActionsPopperItem,
          ]
        : []),
      ...sharedPopperItems,
      ...(selectedApplication?.status === DogBreederApplicationStatus.DONE ||
      selectedApplication?.status === DogBreederApplicationStatus.CONTACT_AVAILABLE
        ? [
            {
              label: translate('common.buttons.messages'),
              iconName: 'comments',
              onClick: () => {
                popper.close();
                if (!selectedApplication?.id || !otherAccountId) throw new Error();
                if (existingConversationId) {
                  navigateToRoute('mtMessages', { params: [otherAccountId.toString()] });
                } else {
                  show();
                }
              },
            } as ActionsPopperItem,
          ]
        : []),
      ...(selectedApplication?.status === DogBreederApplicationStatus.AWAITING_PAYMENT
        ? [
            {
              label: translate('common.buttons.cancel'),
              iconName: 'ban',
              onClick: () => {
                popper.close();
                if (!selectedApplication?.id || !otherAccountId) throw new Error();
                cancelClickHandler();
              },
            } as ActionsPopperItem,
          ]
        : []),
    ];
  }, [
    cancelClickHandler,
    checkoutApplicationClickHandler,
    existingConversationId,
    navigateToRoute,
    otherAccountId,
    popper,
    selectedApplication?.id,
    selectedApplication?.status,
    sharedPopperItems,
    show,
    showOwnModal,
    translate,
  ]);

  return (
    <>
      <MtApplicationsPage
        title={translate('mt.buyer.applications.title')}
        emptyTitle={translate('mt.applications.empty.titles.APPLICATIONS')}
        emptyText={translate('mt.applications.empty.text')}
        loading={isLoading}
        topCards={formattedNewApplicationsCards}
        cards={formattedApplicationsCards}
        columnTitles={[
          translate('glossary.breeder'),
          translate('glossary.breed'),
          translate('glossary.status'),
          translate('glossary.application'),
        ]}
        breederItems={breeders.map((b) => formatBreeder(b))}
        personType="buyer"
        showArchived={showArchived}
        onToggleShowArchived={setShowArchived}
        onOpenSuggestionsModal={breeders?.length ? showSuggestionsModal : undefined}
      />

      <ActionsPopper popper={popper.popper} items={popperItems} />
      <MtApplicationsBuyerPayModalController {...checkoutApplicationModalProps} />
      <ConfirmationModal
        onConfirm={confirmHandler}
        modal={confirmCancelApplicationModal}
        text={translate('mt.buyer.applications.confirmCancel')}
      />
      <MtApplicationsOwnModalController
        modal={ownModal}
        applicationId={selectedApplication?.id}
        breeder={selectedApplication?.dogBreeder}
        onRedirect={() => navigateToRoute('mtAnimals')}
      />
      <MtApplicationsSuggestionModalController
        modal={suggestionsModal}
        suggestionRows={formatBreederSuggestions(breeders)}
      />
    </>
  );
};
